import { Injectable, NgZone } from '@angular/core';
import {
  COUNTY_LEVEL_LAYER,
  H3_RES5_LEVEL_LAYER,
  H3_RES7_LEVEL_LAYER,
  LayerStoreService,
  LEVEL_LAYER,
  MIN_RES_7_ZOOM,
  STATE_LEVEL_LAYER,
} from '../../services/layer-store.service';
import { MapBoxService } from '../../mapbox.service';
import { LevelLayers } from './levels';

// interface SelectedLevelData {
//     level: keyof typeof LevelLayers;
//     preferredZoom: number | undefined;
// }

interface SelectedLevelData {
  level: LEVEL_LAYER; // Changed from keyof typeof LevelLayers
  preferredZoom: number | undefined;
}

@Injectable({
  providedIn: 'root',
})
export class LevelSelectionService {
  private readonly selections: { [value in LevelLayers]: SelectedLevelData } = {
    [LevelLayers.featured_cell_state]: {
      level: STATE_LEVEL_LAYER,
      preferredZoom: 3.05,
    },
    [LevelLayers.featured_cell_county]: {
      level: COUNTY_LEVEL_LAYER,
      preferredZoom: undefined,
    },
    [LevelLayers.featured_cell_h3_res_5]: {
      level: H3_RES5_LEVEL_LAYER, // Placeholder, will be updated based on zoom level
      preferredZoom: undefined,
    },
    [LevelLayers.featured_cell_h3_res_7]: {
      level: H3_RES7_LEVEL_LAYER, // Placeholder, will be updated based on zoom level
      preferredZoom: MIN_RES_7_ZOOM,
    },
  };

  constructor(
    private layerStore: LayerStoreService,
    private mapService: MapBoxService,
    private zone: NgZone
  ) {}

  public changeLevelBySelection(selection: LevelLayers): void {
    this.selectLevel(this.getSelectionByName(selection).level);

    //skip action if we're already zoomed closer than minimum Cells zoom
    if (
      this.mapService.map.getZoom() > MIN_RES_7_ZOOM &&
      this.layerStore.activeLevel.getValue() === H3_RES7_LEVEL_LAYER
    )
      return;

    this.changeZoomByLevelSelection(
      this.getSelectionByName(selection).preferredZoom
    );
  }

  /**
   * Makes `level` active and records it as a deliberate choice, so the
   * zoom-driven switching in `LayerStoreService` respects it rather than
   * overruling it on the next camera move. Shared with the search: finding a
   * state or a county switches the tool exactly as picking it from the
   * dropdown does, minus the dropdown's own zoom step - the search has its own
   * camera to fly.
   */
  public selectLevel(level: LEVEL_LAYER): void {
    this.layerStore.handleLevelChange(level);

    this.layerStore.isLevelLayerSelected = true;
    this.layerStore.lastSelectedLevel = level;
  }

  private changeZoomByLevelSelection(zoom: number | undefined): void {
    //smooth transition between zoom
    if (zoom) {
      // Outside the zone: the map is built outside it, but a camera animation
      // started from in here inherits *this* zone for its whole flight, and
      // GL JS drives that flight with requestAnimationFrame - a patched one,
      // so every frame of it would run application-wide change detection.
      this.zone.runOutsideAngular(() =>
        this.mapService.map.easeTo({
          duration: 1000,
          zoom: zoom,
          center: this.mapService.map.getCenter(),
        })
      );
    }
  }

  private getSelectionByName(name: LevelLayers): SelectedLevelData {
    return this.selections[name];
  }
}
